import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { TFunction } from "i18next";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, BADGE, FONT_SIZE, HOLIDAY_GROUPS, INK, NEUTRAL } from "../../../theme/tokens";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import { EyebrowLabel } from "../../UIToolkit/molecules/EyebrowLabel";
import type { Holiday } from "./holidayApi";
import { formatHolidayDate, worksHoliday } from "./holidayFormat";

type Props = {
	/** "list" = the popup's list with dates; "pills" = one pill per holiday, for the profile. */
	variant: "list" | "pills";
	/** Undefined while loading. */
	holidays: Holiday[] | undefined;
	isError: boolean;
	/** The group the people are in, or would be in. */
	groupId: string;
	dataTestId: string;
};

/** A small pill: brand-tinted when the person works the holiday, quiet grey when they're off. */
const renderPill = (label: string, working: boolean, dataTestId?: string) => (
	<Box
		component="span"
		data-testid={dataTestId}
		data-working={working}
		sx={(theme) => ({
			display: "inline-flex",
			alignItems: "center",
			px: 1,
			py: 0.25,
			borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
			fontSize: FONT_SIZE.CAPTION,
			fontWeight: working ? 600 : 500,
			whiteSpace: "nowrap",
			color: working ? theme.palette.primary.dark : INK.MUTED,
			backgroundColor: working ? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) : NEUTRAL[150],
		})}
	>
		{label}
	</Box>
);

/** What shows instead of the holidays while they load, if they failed to load, or when there are none. */
const renderNoHolidays = (t: TFunction, holidays: Holiday[] | undefined, isError: boolean): ReactNode => {
	if (isError) return <Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "error.main" }}>{t("holidays.groupChange.loadError")}</Typography>;
	if (holidays === undefined) return <JumpingDots size={6} />;
	return <Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("holidays.groupChange.noUpcoming")}</Typography>;
};

const renderPills = (t: TFunction, holidays: Holiday[], groupId: string, dataTestId: string) => (
	<Box component="ul" data-testid={dataTestId} sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
		{holidays.map((holiday) => {
			const working = worksHoliday(holiday, groupId);
			const label = t("holidays.profile.holidayPill", {
				name: holiday.name,
				year: holiday.date.year,
				state: t(working ? "holidays.working" : "holidays.off"),
			});
			return <li key={holiday.id}>{renderPill(label, working, `${dataTestId}-${holiday.id}`)}</li>;
		})}
	</Box>
);

const renderList = (t: TFunction, holidays: Holiday[], groupId: string, dataTestId: string) => (
	<Box
		component="ul"
		sx={{
			m: 0,
			p: 0,
			listStyle: "none",
			border: `1px solid ${NEUTRAL[200]}`,
			borderRadius: 1,
			maxHeight: HOLIDAY_GROUPS.WORKING_LIST_MAX_HEIGHT_PX,
			overflowY: "auto",
		}}
	>
		{holidays.map((holiday) => {
			const working = worksHoliday(holiday, groupId);
			return (
				<Box
					component="li"
					key={holiday.id}
					data-testid={`${dataTestId}-${holiday.id}`}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						px: 1.5,
						py: 1,
						fontSize: FONT_SIZE.EMPHASIS,
						"&:not(:last-of-type)": { borderBottom: `1px solid ${NEUTRAL[150]}` },
					}}
				>
					<Box component="span" sx={{ fontWeight: 600, color: INK.PRIMARY, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{holiday.name}
					</Box>
					<Box component="span" sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, whiteSpace: "nowrap" }}>
						{formatHolidayDate(holiday.date)}
					</Box>
					<Box component="span" sx={{ ml: "auto" }}>
						{renderPill(t(working ? "holidays.working" : "holidays.off"), working)}
					</Box>
				</Box>
			);
		})}
	</Box>
);

/** The upcoming holidays and whether `groupId` works each one or is off. */
export const HolidayWorkingList: FC<Props> = ({ variant, holidays, isError, groupId, dataTestId }) => {
	const { t } = useTranslation();
	const hasHolidays = !isError && holidays !== undefined && holidays.length > 0;
	const render = variant === "pills" ? renderPills : renderList;
	const content = hasHolidays ? render(t, holidays, groupId, dataTestId) : renderNoHolidays(t, holidays, isError);
	if (variant === "pills") return content;
	return (
		<Box data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<EyebrowLabel component="h3">{t("holidays.upcoming")}</EyebrowLabel>
			{content}
		</Box>
	);
};
